import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Megaphone, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import StageSection from "./StageSection";

const CanvassStage = () => {
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["home-canvass-prichard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("curated_posts")
        .select("id,slug,title,summary,cover_image_url,source_org_name,source_url,tags,category,published_at")
        .eq("status", "published")
        .or("tags.cs.{Prichard},title.ilike.%prichard%,source_org_name.ilike.%prichard%")
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(3);
      if (error) throw error;
      if ((data ?? []).length > 0) return data;
      // fallback: most recent curated
      const { data: fb } = await supabase
        .from("curated_posts")
        .select("id,slug,title,summary,cover_image_url,source_org_name,source_url,tags,category,published_at")
        .eq("status", "published")
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(3);
      return fb ?? [];
    },
  });

  return (
    <StageSection
      id="canvass"
      number="01"
      eyebrow="Canvass · Advocacy"
      title="Start by listening to the people the plan is for."
      intro="Before any initiative under the Financial Empowerment Action Plan gets a brief, we surface the voices already telling the story — small business owners, creators, and organizers in Prichard and Mobile's priority zips. Their lived signal is the first input."
      icon={Megaphone}
      ctaLabel="Browse community voices"
      ctaHref="/archive?source=curated"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card h-64 animate-pulse" />
            ))
          : posts.map((p: any) => (
              <Link
                key={p.id}
                to={`/archive/curated/${p.slug}`}
                className="group rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-all flex flex-col"
              >
                {p.cover_image_url ? (
                  <div className="aspect-[16/10] overflow-hidden bg-muted">
                    <img
                      src={p.cover_image_url}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                ) : null}
                <div className="p-4 flex-1 flex flex-col">
                  <Badge variant="outline" className="self-start mb-2 text-[10px]">
                    EM Curated
                  </Badge>
                  <h3 className="font-display font-semibold text-foreground text-base leading-snug mb-2 line-clamp-3">
                    {p.title}
                  </h3>
                  <div className="mt-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                    <ExternalLink className="h-3 w-3" />
                    <span className="truncate">{p.source_org_name}</span>
                  </div>
                </div>
              </Link>
            ))}
      </div>
    </StageSection>
  );
};

export default CanvassStage;